/**
 * Store-rejection vocabulary — why a store said no, and what is still open.
 *
 * Every writer and reader in the rejection lane spells a cause the same way
 * because they all import it from here: the filing path that records a
 * rejection, the operator action that records its remedy, and the read that
 * counts causes across schools and stores (R13, R15).
 *
 * These classes are a `shared-types` union stored as a STRING, never a
 * database enum (KTD6). Subdividing a class — splitting `metadata` into
 * `description` and `keywords`, say — must cost one edit here and no
 * PlanetScale promotion, because the whole point of starting with a small set
 * is being able to grow it once the recorded causes show where the real seams
 * are (R17).
 *
 * Recorded values are therefore NOT guaranteed to be current members. A row
 * written before a subdivision holds whatever the union said that day, and it
 * still has to resolve to a class or the counts taken across the change stop
 * adding up — which is what `storeRejectionCauseClassFor` exists for (AE9).
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-30-1110-feat-store-rejection-triage-plan.md
 */

import { z } from 'zod'

// =============================================================================
// Cause classes
// =============================================================================

/**
 * Why the store rejected the submission, in the broadest groups that can be
 * applied consistently from the very first rejection.
 *
 * The first three are the causes the stores have actually rejected this fleet
 * for; `other` is the explicit catch-all, and it is a real answer rather than a
 * gap — an operator picking it is saying "not one of the recurring three",
 * which is exactly the signal that tells us when a fourth class has earned its
 * place.
 *
 * Broad on purpose. A fine taxonomy invented up front gets applied
 * inconsistently by whoever files, and inconsistent classes make a count worse
 * than no count at all.
 *
 * - `screenshots`  the store objected to the store-listing screenshots
 * - `metadata`     the store objected to listing text — description, subtitle,
 *                  keywords, category, age rating
 * - `brand_rights` the store asked us to prove the right to use the school's
 *                  name or logo. The remedy for this one happens entirely
 *                  outside both stores (a rights letter from the school), which
 *                  is why remedy capture cannot be derived from store signal
 *                  alone
 * - `other`        anything else, including a cause a later subdivision has
 *                  since renamed away
 */
export const STORE_REJECTION_CAUSE_CLASSES = ['screenshots', 'metadata', 'brand_rights', 'other'] as const

export const StoreRejectionCauseClassSchema = z.enum(STORE_REJECTION_CAUSE_CLASSES)
export type StoreRejectionCauseClass = z.infer<typeof StoreRejectionCauseClassSchema>

/**
 * The class every unrecognized stored value falls back to.
 *
 * Named rather than spelled inline at the fallback, so the guarantee AE9 rests
 * on — unknown resolves HERE — is one symbol a reader can follow.
 */
export const STORE_REJECTION_FALLBACK_CAUSE_CLASS: StoreRejectionCauseClass = 'other'

/**
 * Membership as a `Set`, not the `in` operator.
 *
 * `'toString' in someObject` is true, so an `in`-based guard admits every
 * prototype key and a lookup on one yields a function where a class belongs.
 * The same trap `isOperationsEventType` documents, avoided the same way.
 */
const CAUSE_CLASS_MEMBERS: ReadonlySet<string> = new Set(STORE_REJECTION_CAUSE_CLASSES)

/** Whether a value is a CURRENT member of the union. */
export function isStoreRejectionCauseClass(value: unknown): value is StoreRejectionCauseClass {
  return typeof value === 'string' && CAUSE_CLASS_MEMBERS.has(value)
}

/**
 * Resolve a stored cause string to a class. Known values resolve to
 * themselves; everything else resolves to `other`, and nothing throws.
 *
 * Takes `unknown` because that is honestly what the caller has: the value
 * arrives off an append-only event payload, written possibly months and one
 * taxonomy ago, and a reader counting causes across a subdivision cannot
 * assume it is even a string. Throwing here would mean one stale row could
 * fail a whole fleet-wide count — the read this vocabulary exists to make
 * possible (R15, AE9).
 */
export function storeRejectionCauseClassFor(stored: unknown): StoreRejectionCauseClass {
  return isStoreRejectionCauseClass(stored) ? stored : STORE_REJECTION_FALLBACK_CAUSE_CLASS
}

// =============================================================================
// The outstanding bucket (R24)
// =============================================================================

/**
 * The label for rejections that have no recorded remedy yet.
 *
 * Deliberately NOT a member of `STORE_REJECTION_CAUSE_CLASSES`. Awaiting a
 * remedy is a state, not a cause: a rejection has a cause the moment the store
 * reports it and keeps that cause after it is fixed. Admitting this to the
 * union would make every cause count depend on whether the fix had landed yet,
 * and "not fixed yet" would silently eat the cause it was actually rejected
 * for.
 *
 * Readers DERIVE this bucket — a recorded rejection with no matching
 * `app.rejection_remedied` event on its correlation id — and count it
 * alongside the cause classes, never inside them.
 */
export const STORE_REJECTION_OUTSTANDING_BUCKET = 'awaiting_remedy' as const
export type StoreRejectionOutstandingBucket = typeof STORE_REJECTION_OUTSTANDING_BUCKET

// =============================================================================
// Stores
// =============================================================================

/**
 * The two stores that can reject a submission.
 *
 * Deliberately NOT `Platform` (`IOS` / `ANDROID`), which names the device
 * family a build runs on. A rejection is an act of a STOREFRONT — App Store
 * Connect or the Google Play Console — and the two vocabularies stop being
 * interchangeable the moment anything else ships to a platform without going
 * through its store. Keeping them separate also means the recorded event says
 * plainly which reviewer said no, which is the fact R6's ticket and R15's
 * counts are actually about.
 *
 * `submission.rejected` events written by the Apple lane predate this union and
 * carry `platform` rather than `store`; readers map one to the other rather
 * than assuming either is present.
 */
export const STORE_REJECTION_STORES = ['apple', 'google'] as const

export const StoreRejectionStoreSchema = z.enum(STORE_REJECTION_STORES)
export type StoreRejectionStore = z.infer<typeof StoreRejectionStoreSchema>

/** How each store is named to a human — in the ticket, in the channel and on the screen. */
export const STORE_REJECTION_STORE_LABEL: Readonly<Record<StoreRejectionStore, string>> = {
  apple: 'App Store',
  google: 'Google Play'
}

// =============================================================================
// The ticket-filed event payload
// =============================================================================

/**
 * What `app.rejection_ticket_filed` carries (KTD1).
 *
 * This event is the link-back R11 requires — the recorded rejection and the
 * Linear ticket raised for it, joined by one `correlationId` — and it is also
 * the row R25's recurrence check reads. That second job is why `store` and
 * `reportedCause` are on it rather than left to be re-derived from the
 * `submission.rejected` event beside it: the rejection event's payload shape
 * belongs to whichever lane observed it (Apple's carries `platform` and a fixed
 * `rejectionReasons` array), while this one is written by the filing path
 * alone, in one shape, for both stores.
 *
 * `slackPosted` is not bookkeeping. D6 makes a bot that is not a member of the
 * private `#app-operations` channel fail SILENTLY, and the operator still gets
 * their ticket either way — so a systematically unannounced rejection has no
 * visible symptom anywhere else. Writing the outcome here is what lets a reader
 * report it (R9).
 */
export const StoreRejectionTicketFiledPayloadSchema = z.object({
  store: StoreRejectionStoreSchema,
  /** The school the ticket was filed for, or `null` when it went to the unattributed queue (R22). */
  schoolPublicId: z.string().nullable(),
  /** Whatever the store reported about the cause. Apple's is a fixed constant, not a description. */
  reportedCause: z.string(),
  /** Linear's own key, e.g. `SCH-1234` — what a human quotes. */
  issueIdentifier: z.string(),
  issueId: z.string(),
  issueUrl: z.string(),
  /** Linear's numeric priority, derived per R10 (1 = urgent, 2 = high). */
  priority: z.number().int(),
  /** Whether this rejection blocks a school's FIRST launch on that store (R10). */
  firstLaunch: z.boolean(),
  /** Whether the `#app-operations` announcement actually landed (D6). */
  slackPosted: z.boolean(),
  /** A safe reason when the announcement did not land; `null` when it did. */
  slackError: z.string().nullable(),
  /**
   * The correlation id of the EARLIER rejection whose still-open ticket this
   * one was folded into (R25), or `null` when this filing raised the ticket.
   *
   * A recurrence comments on the open ticket and files nothing new, but it must
   * still leave a ticket-filed event on its OWN correlation id — otherwise the
   * new rejection carries no ticket key at all: the outstanding list renders it
   * as an unticketed row and the next recurrence cannot see it as ticketed
   * (R23). This field is what keeps that event honest about being a pointer
   * rather than a second filing, and it is why such a row is excluded from the
   * failed-announcement read: a recurrence records `slackPosted: false` because
   * it deliberately announces nothing, not because Slack refused it.
   */
  recurrenceOf: z.string().nullable().default(null)
})

export type StoreRejectionTicketFiledPayload = z.infer<typeof StoreRejectionTicketFiledPayloadSchema>

// =============================================================================
// The remedied event payload
// =============================================================================

/**
 * What `app.rejection_remedied` carries.
 *
 * Two writers append it: the operator's record-remedy dialog, and a remedy run
 * that the store was observed to accept (SCH-2889 KTD9). The event dedupes on
 * the correlation id and the first write stands, so it records the outcome
 * once and never a run's progress; that is `app.rejection_remedy_started` and
 * `app.rejection_remedy_ended`.
 *
 * `templateId` is set only by a remedy run, and is optional so every event
 * written before the Rejections tab still parses. `store` is nullable because
 * a rejection recorded without a storefront still takes a remedy.
 */
export const StoreRejectionRemediedPayloadSchema = z.object({
  causeClass: StoreRejectionCauseClassSchema,
  remedy: z.string(),
  resubmitted: z.boolean(),
  store: StoreRejectionStoreSchema.nullable(),
  templateId: z.string().optional()
})
export type StoreRejectionRemediedPayload = z.infer<typeof StoreRejectionRemediedPayloadSchema>

// =============================================================================
// The reviewer's text (SCH-2889)
// =============================================================================

/**
 * Where a rejection's reviewer text came from. A `pasted` text is never
 * overwritten by a later fetch (R7).
 *
 * - `email`    Play's rejection alert email
 * - `fetched`  Apple's Resolution Center, read through the stored web session
 * - `pasted`   an operator pasted it on the Rejections tab
 */
export const STORE_REJECTION_TEXT_SOURCES = ['email', 'fetched', 'pasted'] as const
export const StoreRejectionTextSourceSchema = z.enum(STORE_REJECTION_TEXT_SOURCES)
export type StoreRejectionTextSource = z.infer<typeof StoreRejectionTextSourceSchema>

/**
 * Why a rejection has, or lacks, its text. The text is fetched on demand, so
 * this is the outcome of the latest read, not a stored column.
 *
 * - `fetched`          a text is available
 * - `not_fetched`      no fetch has been attempted for this rejection
 * - `pending`          a fetch is in flight
 * - `session_expired`  the Apple web session was not usable
 * - `no_message`       the store holds no reviewer message for this rejection
 * - `mismatch`         a message was found but did not match this version and time
 * - `failed`           the fetch itself failed
 */
export const STORE_REJECTION_FETCH_STATES = [
  'fetched',
  'not_fetched',
  'pending',
  'session_expired',
  'no_message',
  'mismatch',
  'failed'
] as const
export const StoreRejectionFetchStateSchema = z.enum(STORE_REJECTION_FETCH_STATES)
export type StoreRejectionFetchState = z.infer<typeof StoreRejectionFetchStateSchema>

/** A store guideline number as reviewers cite it, e.g. Apple's `2.3.10`. */
export const StoreRejectionGuidelineCodeSchema = z.string().min(1).max(32)

/**
 * The fetch outcomes a rejection keeps when Apple's Resolution Center gave no
 * text for it (SCH-2889 U3). Cached like a text, so a tab open reuses the
 * answer instead of fetching again on every visit; "Fetch again" asks anew.
 * `pending` and `not_fetched` are never cached: they describe no attempt that
 * finished.
 */
export const STORE_REJECTION_FETCH_OUTCOMES = [
  'session_expired',
  'no_message',
  'mismatch',
  'failed'
] as const satisfies readonly StoreRejectionFetchState[]
export const StoreRejectionFetchOutcomeSchema = z.enum(STORE_REJECTION_FETCH_OUTCOMES)
export type StoreRejectionFetchOutcome = z.infer<typeof StoreRejectionFetchOutcomeSchema>

/** A captured text: from the Play email, the Resolution Center or a paste. */
const StoreRejectionTextEntrySchema = z.object({
  text: z.string(),
  source: StoreRejectionTextSourceSchema,
  guidelineCodes: z.array(StoreRejectionGuidelineCodeSchema),
  /** ISO-8601. When the text was captured. */
  capturedAt: z.string().datetime()
})

/**
 * A fetch that finished without a text: why, and when. Always `fetched`, the
 * one source a fetch writes, so the paste-wins guard treats it like any other
 * fetch. It never replaces a text already cached (see `writeRejectionText`).
 */
const StoreRejectionFetchOutcomeEntrySchema = z.object({
  // Defaulted because the cache is written with JSON_MERGE_PATCH, which drops a
  // key whose patch value is null: the stored entry has no `text` at all.
  text: z.null().default(null),
  source: z.literal('fetched'),
  fetchState: StoreRejectionFetchOutcomeSchema,
  guidelineCodes: z.array(StoreRejectionGuidelineCodeSchema).max(0),
  /** ISO-8601. When the fetch reported. */
  capturedAt: z.string().datetime()
})

/**
 * One cached entry, kept on the rejected version's row at
 * `MobileAppStoreVersion.detail.rejectionTexts[correlationId]`: a reviewer
 * text, or the outcome of a fetch that found none (`text: null`).
 *
 * Keyed by correlation id because Apple reuses one store-version row across a
 * rejection and its resubmission: a second rejection of the same version adds
 * a key rather than overwriting the first text. The text never goes into an
 * operations-event payload or a log line (R8).
 */
export const StoreRejectionTextCacheEntrySchema = z.union([
  StoreRejectionTextEntrySchema,
  StoreRejectionFetchOutcomeEntrySchema
])
export type StoreRejectionTextCacheEntry = z.infer<typeof StoreRejectionTextCacheEntrySchema>

/** The `detail` key the cache lives under. */
export const STORE_REJECTION_TEXTS_DETAIL_KEY = 'rejectionTexts' as const

export const StoreRejectionTextCacheSchema = z.record(z.string(), StoreRejectionTextCacheEntrySchema)
export type StoreRejectionTextCache = z.infer<typeof StoreRejectionTextCacheSchema>

/**
 * Read the cached texts off a `MobileAppStoreVersion.detail` value.
 *
 * Takes `unknown` because `detail` is a JSON sidecar several writers touch. An
 * entry that does not parse is dropped on its own, so one malformed key cannot
 * hide every other rejection's text; a missing or malformed cache reads as
 * empty.
 */
export function readStoreRejectionTextCache(detail: unknown): StoreRejectionTextCache {
  if (typeof detail !== 'object' || detail === null) return {}
  const cache: unknown = (detail as Record<string, unknown>)[STORE_REJECTION_TEXTS_DETAIL_KEY]
  if (typeof cache !== 'object' || cache === null || Array.isArray(cache)) return {}
  const entries: StoreRejectionTextCache = {}
  for (const [correlationId, value] of Object.entries(cache)) {
    const parsed = StoreRejectionTextCacheEntrySchema.safeParse(value)
    if (parsed.success) entries[correlationId] = parsed.data
  }
  return entries
}

// =============================================================================
// Remedy runs (SCH-2889 KTD9)
// =============================================================================

/**
 * A remedy run's lifecycle. Runs are derived from the
 * `app.rejection_remedy_started` / `app.rejection_remedy_ended` events and the
 * setup drive or prompt job they name, not stored in a table.
 *
 * - `applying`        the template's fix is being written
 * - `driving`         the scoped setup drive is running
 * - `awaiting_store`  the resubmit node finished; waiting to observe the store
 * - `resubmitted`     the store was observed holding the resubmission (R11)
 * - `failed`          a step failed, or no store confirmation came in 24 hours
 * - `no_change`       a prompt template produced no change (R15)
 * - `superseded`      the rejection was superseded before the run finished
 */
export const STORE_REJECTION_REMEDY_RUN_STATUSES = [
  'applying',
  'driving',
  'awaiting_store',
  'resubmitted',
  'failed',
  'no_change',
  'superseded'
] as const
export const StoreRejectionRemedyRunStatusSchema = z.enum(STORE_REJECTION_REMEDY_RUN_STATUSES)
export type StoreRejectionRemedyRunStatus = z.infer<typeof StoreRejectionRemedyRunStatusSchema>

/** The statuses of a run that has not ended. Every other status is terminal. */
export const STORE_REJECTION_REMEDY_RUN_OPEN_STATUSES = [
  'applying',
  'driving',
  'awaiting_store'
] as const satisfies readonly StoreRejectionRemedyRunStatus[]

const OPEN_RUN_STATUS_MEMBERS: ReadonlySet<string> = new Set(STORE_REJECTION_REMEDY_RUN_OPEN_STATUSES)

/** Whether a run in this status is still open, which blocks another run for the same app and store (R13). */
export function isStoreRejectionRemedyRunOpen(status: StoreRejectionRemedyRunStatus): boolean {
  return OPEN_RUN_STATUS_MEMBERS.has(status)
}

/** The terminal statuses, the only ones `app.rejection_remedy_ended` may carry. */
export const STORE_REJECTION_REMEDY_RUN_END_STATUSES = [
  'resubmitted',
  'failed',
  'no_change',
  'superseded'
] as const satisfies readonly StoreRejectionRemedyRunStatus[]
export const StoreRejectionRemedyRunEndStatusSchema = z.enum(STORE_REJECTION_REMEDY_RUN_END_STATUSES)
export type StoreRejectionRemedyRunEndStatus = z.infer<typeof StoreRejectionRemedyRunEndStatusSchema>

// =============================================================================
// Remedy templates (SCH-2889 KTD7)
// =============================================================================

/**
 * A template id: the store, a dot, then a kebab-case slug, e.g.
 * `apple.regenerate-screenshots`. The catalog itself lives in the backend
 * (`services/mobile-apps/rejection-remedies/catalog.ts`); this pattern only
 * refuses a malformed id at the request boundary, before any catalog lookup.
 */
export const STORE_REJECTION_TEMPLATE_ID_PATTERN = /^(apple|google)\.[a-z0-9]+(?:-[a-z0-9]+)*$/
export const StoreRejectionTemplateIdSchema = z.string().max(64).regex(STORE_REJECTION_TEMPLATE_ID_PATTERN)

/**
 * How a template fixes the rejection.
 *
 * - `graph`   reset listed setup-graph nodes and run them again
 * - `prompt`  rewrite one text field with a model, then resubmit
 */
export const STORE_REJECTION_REMEDY_ACTION_KINDS = ['graph', 'prompt'] as const
export const StoreRejectionRemedyActionKindSchema = z.enum(STORE_REJECTION_REMEDY_ACTION_KINDS)
export type StoreRejectionRemedyActionKind = z.infer<typeof StoreRejectionRemedyActionKindSchema>

/**
 * The one field a prompt template may write (KTD8): Apple's App Review notes,
 * or the store listing description.
 */
export const STORE_REJECTION_PROMPT_TARGETS = ['apple_review_notes', 'store_listing_description'] as const
export const StoreRejectionPromptTargetSchema = z.enum(STORE_REJECTION_PROMPT_TARGETS)
export type StoreRejectionPromptTarget = z.infer<typeof StoreRejectionPromptTargetSchema>
